import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowUpRight, LayoutGrid } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { scrollToSection } from "@/lib/scroll";
import { mediaSrc } from "@/lib/media";
import { CATALOG_CATS } from "@/data/catalog";
import { useQuickView } from "@/components/QuickViewProvider";
import { FavoriteButton } from "@/components/FavoriteButton";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const Catalog = () => {
  const { t } = useLang();
  const { products, open } = useQuickView();
  const [cat, setCat] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("default");
  const [visible, setVisible] = useState(24);

  useEffect(() => {
    setVisible(24);
  }, [cat, query]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter(
      (p) =>
        (cat === "all" || p.category === cat) &&
        (!q || p.title.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q)),
    );
  }, [products, cat, query]);

  const sorted = useMemo(() => {
    if (sort === "default") return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: "base" }));
    if (sort === "za") arr.reverse();
    return arr;
  }, [filtered, sort]);

  const shown = useMemo(() => sorted.slice(0, visible), [sorted, visible]);

  const badgeLabel = (b) => (b === "hit" ? t("catalog.badges.hit") : b === "new" ? t("catalog.badges.new") : "");
  const badgeCls = (b) => (b === "hit" ? "bg-gold text-black" : "bg-emerald-500 text-white");

  const counts = useMemo(() => {
    const c = {};
    products.forEach((p) => {
      c[p.category] = (c[p.category] || 0) + 1;
    });
    return c;
  }, [products]);

  return (
    <section id="catalog" className="relative py-16 md:py-32 px-6 md:px-12 bg-card border-y border-border">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <Eyebrow>{t("catalog.badge")}</Eyebrow>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl text-foreground leading-tight">
                {t("catalog.title")}
              </h2>
              <p className="mt-4 text-muted-foreground font-sans leading-relaxed">{t("catalog.subtitle")}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <div className="relative w-full sm:w-64 shrink-0">
                <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="catalog-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("catalog.search")}
                  className="w-full bg-background border border-border focus:border-gold rounded-sm pl-10 pr-3 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors font-sans"
                />
              </div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger
                  data-testid="catalog-sort"
                  className="w-full sm:w-52 h-auto bg-background border border-border focus:border-gold data-[state=open]:border-gold rounded-sm px-3 py-3 text-sm text-foreground font-sans shadow-none focus:ring-0 focus:ring-offset-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border font-sans">
                  <SelectItem value="default" data-testid="catalog-sort-default">{t("catalog.sortDefault")}</SelectItem>
                  <SelectItem value="az" data-testid="catalog-sort-az">{t("catalog.sortAz")}</SelectItem>
                  <SelectItem value="za" data-testid="catalog-sort-za">{t("catalog.sortZa")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Reveal>

        {/* Category chips */}
        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2.5" data-testid="catalog-cats">
            <button
              data-testid="catalog-cat-all"
              onClick={() => setCat("all")}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs font-sans uppercase tracking-[0.12em] rounded-full border transition-all ${
                cat === "all" ? "border-gold text-gold bg-gold/10" : "border-border text-muted-foreground hover:border-gold/50 hover:text-foreground"
              }`}
            >
              <LayoutGrid size={15} /> {t("catalog.all")}
            </button>
            {CATALOG_CATS.filter(({ slug }) => counts[slug]).map(({ slug, Icon }) => (
              <button
                key={slug}
                data-testid={`catalog-cat-${slug}`}
                onClick={() => setCat(slug)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs font-sans uppercase tracking-[0.12em] rounded-full border transition-all ${
                  cat === slug ? "border-gold text-gold bg-gold/10" : "border-border text-muted-foreground hover:border-gold/50 hover:text-foreground"
                }`}
              >
                <Icon size={15} />
                {t(`catalog.cats.${slug}`)}
                {counts[slug] ? <span className="text-[10px] opacity-60">({counts[slug]})</span> : null}
              </button>
            ))}
          </div>
        </Reveal>

        {/* Products grid */}
        {filtered.length === 0 ? (
          <p className="mt-12 text-muted-foreground font-sans" data-testid="catalog-empty">
            {t("catalog.empty")}
          </p>
        ) : (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="catalog-grid">
            <AnimatePresence mode="popLayout">
              {shown.map((p, i) => (
                <motion.div
                  key={p.product_id}
                  data-testid={`product-${i}`}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  onClick={() => open(p)}
                  className="group bg-background border border-border hover:border-gold/50 rounded-sm overflow-hidden transition-colors flex flex-col cursor-pointer"
                >
                  <div className="relative h-52 overflow-hidden bg-muted">
                    {p.image ? (
                      <img
                        src={mediaSrc(p.image)}
                        alt={p.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : null}
                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-black/60 text-gold px-2 py-1 rounded-sm">
                      {t(`catalog.cats.${p.category}`)}
                    </span>
                    <div className="absolute top-3 right-3 flex flex-col items-end gap-2">
                      <FavoriteButton product={p} context="catalog" className="w-9 h-9" size={16} />
                      {p.badge ? (
                        <span
                          data-testid={`product-badge-${i}`}
                          className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded-sm ${badgeCls(p.badge)}`}
                        >
                          {badgeLabel(p.badge)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-serif text-xl text-foreground">{p.title}</h3>
                    {p.description ? (
                      <p className="mt-2 text-sm text-muted-foreground font-sans leading-relaxed line-clamp-3">
                        {p.description}
                      </p>
                    ) : null}
                    <button
                      data-testid={`product-quote-${i}`}
                      onClick={(e) => { e.stopPropagation(); scrollToSection("contact"); }}
                      className="mt-4 inline-flex items-center gap-1.5 text-xs font-sans font-semibold uppercase tracking-[0.12em] text-gold hover:gap-2.5 transition-all self-start"
                    >
                      {t("catalog.quote")} <ArrowUpRight size={15} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {filtered.length > visible && (
          <div className="mt-12 flex flex-col items-center gap-3" data-testid="catalog-more-wrap">
            <button
              data-testid="catalog-more"
              onClick={() => setVisible((v) => v + 24)}
              className="inline-flex items-center gap-2 px-8 py-3 text-xs font-sans uppercase tracking-[0.14em] rounded-full border border-gold/60 text-gold hover:bg-gold hover:text-black transition-all duration-300"
            >
              {t("catalog.more")}
            </button>
            <span className="text-xs text-muted-foreground font-sans">
              {shown.length} / {filtered.length}
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

export default Catalog;
